import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { EncryptionService } from './encryptionService';

const JOURNALS_COLLECTION = 'journals';

export interface JournalEntry {
  id: string;
  userId: string;
  title: string;
  content: string;
  labels: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isLocked?: boolean; // Flag for decryption failure
}

export interface JournalInput {
  title?: string;
  content?: string;
  labels?: string[];
}

export interface JournalUpdate {
  title?: string;
  content?: string;
  labels?: string[];
}

// Create a new journal entry
export const createJournalEntry = async (
  userId: string,
  journalData: JournalInput,
  passkey: string
): Promise<string> => {
  try {
    // Encrypt title and content before storing
    const encryptedTitle = EncryptionService.encrypt(
      journalData.title || 'Untitled Journal',
      passkey
    );
    const encryptedContent = EncryptionService.encrypt(
      journalData.content || '',
      passkey
    );

    const journalRef = await addDoc(collection(db, JOURNALS_COLLECTION), {
      userId,
      title: encryptedTitle,
      content: encryptedContent,
      labels: journalData.labels || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return journalRef.id;
  } catch (error) {
    console.error('Error creating journal entry:', error);
    throw error;
  }
};

// Get all journal entries for a user
export const getUserJournals = async (
  userId: string,
  passkey: string,
  labelFilter: string | null = null
): Promise<JournalEntry[]> => {
  try {
    let q;

    if (labelFilter && labelFilter !== 'all') {
      q = query(
        collection(db, JOURNALS_COLLECTION),
        where('userId', '==', userId),
        where('labels', 'array-contains', labelFilter),
        orderBy('updatedAt', 'desc')
      );
    } else {
      q = query(
        collection(db, JOURNALS_COLLECTION),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const journals: JournalEntry[] = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      try {
        // Handle empty strings - they can't be decrypted
        const decryptedTitle = data.title ? EncryptionService.decrypt(data.title, passkey) : '';
        const decryptedContent = data.content ? EncryptionService.decrypt(data.content, passkey) : '';

        journals.push({
          id: docSnap.id,
          ...data,
          title: decryptedTitle || 'Untitled',
          content: decryptedContent || '',
          isLocked: false,
        } as JournalEntry);
      } catch (error) {
        console.error(`Error decrypting journal ${docSnap.id}:`, error);
        journals.push({
          id: docSnap.id,
          ...data,
          title: '🔒 Locked',
          content: '',
          isLocked: true,
        } as JournalEntry);
      }
    });

    return journals;
  } catch (error) {
    console.error('Error getting journals:', error);
    throw error;
  }
};

// Get a single journal entry
export const getJournalById = async (
  journalId: string,
  passkey: string
): Promise<JournalEntry | null> => {
  try {
    const journalRef = doc(db, JOURNALS_COLLECTION, journalId);
    const docSnap = await getDoc(journalRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    try {
      const decryptedTitle = data.title ? EncryptionService.decrypt(data.title, passkey) : '';
      const decryptedContent = data.content ? EncryptionService.decrypt(data.content, passkey) : '';

      return {
        id: docSnap.id,
        ...data,
        title: decryptedTitle || 'Untitled',
        content: decryptedContent || '',
        isLocked: false,
      } as JournalEntry;
    } catch (error) {
      return {
        id: docSnap.id,
        ...data,
        title: '🔒 Locked',
        content: '',
        isLocked: true,
      } as JournalEntry;
    }
  } catch (error) {
    console.error('Error getting journal:', error);
    throw error;
  }
};

// Update a journal entry
export const updateJournalEntry = async (
  journalId: string,
  updates: JournalUpdate,
  passkey: string
): Promise<void> => {
  try {
    const journalRef = doc(db, JOURNALS_COLLECTION, journalId);
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (updates.title !== undefined) {
      updateData.title = EncryptionService.encrypt(updates.title, passkey);
    }
    if (updates.content !== undefined) {
      updateData.content = EncryptionService.encrypt(updates.content, passkey);
    }
    if (updates.labels !== undefined) {
      updateData.labels = updates.labels;
    }

    await updateDoc(journalRef, updateData);
  } catch (error) {
    console.error('Error updating journal entry:', error);
    throw error;
  }
};

// Delete a journal entry
export const deleteJournalEntry = async (journalId: string): Promise<void> => {
  try {
    const journalRef = doc(db, JOURNALS_COLLECTION, journalId);
    await deleteDoc(journalRef);
  } catch (error) {
    console.error('Error deleting journal entry:', error);
    throw error;
  }
};

// Get all journal labels for a user
export const getJournalLabels = async (userId: string): Promise<string[]> => {
  try {
    const q = query(
      collection(db, JOURNALS_COLLECTION),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const labelsSet = new Set<string>();

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.labels && Array.isArray(data.labels)) {
        data.labels.forEach((label: string) => labelsSet.add(label));
      }
    });

    return Array.from(labelsSet).sort();
  } catch (error) {
    console.error('Error getting journal labels:', error);
    throw error;
  }
};

// Re-encrypt all journal entries with new passkey
export const reEncryptJournalEntries = async (
  userId: string,
  oldPasskey: string,
  newPasskey: string,
  onProgress?: (current: number, total: number) => void
): Promise<{ success: number; failed: number }> => {
  try {
    const entries = await getUserJournals(userId, oldPasskey);
    let success = 0;
    let failed = 0;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];

      if (entry.isLocked) {
        failed++;
        onProgress?.(i + 1, entries.length);
        continue;
      }

      try {
        await updateJournalEntry(
          entry.id,
          { title: entry.title, content: entry.content },
          newPasskey
        );
        success++;
      } catch (error) {
        console.error(`Failed to re-encrypt journal ${entry.id}:`, error);
        failed++;
      }

      onProgress?.(i + 1, entries.length);
    }

    return { success, failed };
  } catch (error) {
    console.error('Error re-encrypting journal entries:', error);
    throw error;
  }
};

